import React from 'react';
import { styled } from 'styled-components';

/*
 * Lightweight Markdown for content fields served by Fairchain's GeoNode
 * (intro_text, background_intro, background_text_N on the group-summary layers).
 *
 * The content team writes `**bold**`, `*italic*` and blank-line paragraph breaks
 * straight into the database, and some older rows carry a literal `<br>`. Until
 * 2026-09-05 those fields were rendered as plain text, so the stars showed on the
 * public site.
 *
 * 2026-10-02 (founder request): `[link text](https://example.com)` is rendered as
 * a link that opens in a new tab. Only http:// and https:// addresses become
 * links; anything else (javascript:, data:, mailto:, relative paths, a broken
 * address) is left on the page as the plain text that was typed.
 *
 * Everything here is built as React elements from a small tokenizer - never
 * dangerouslySetInnerHTML, never a regex-to-HTML string. Any character that is
 * not one of the markers above is rendered verbatim (React escapes it), so
 * nothing in the upstream text can inject markup into the page. The upstream is
 * untrusted (CLAUDE.md §8); this component is part of the boundary.
 */

export type InlineNode =
  | { type: 'text'; value: string }
  | { type: 'strong'; children: InlineNode[] }
  | { type: 'em'; children: InlineNode[] }
  | { type: 'link'; href: string; children: InlineNode[] };

/** One paragraph = its lines (joined with <br /> when rendered). */
export type ParagraphLines = InlineNode[][];

// Legacy rows contain a literal <br>, <br/> or <br /> where a line break is meant.
const LEGACY_BR = /<br\s*\/?>/gi;
// Blank line (possibly with stray spaces on it) = paragraph break.
const PARAGRAPH_BREAK = /\n\s*\n/;

const isWhitespace = (ch: string | undefined) => ch !== undefined && /\s/.test(ch);

/**
 * Find the closing marker for an emphasis run that opened at `from`.
 * Rules (deliberately stricter than CommonMark, to avoid false positives in
 * prose like "2 * 3" or bullet-style "* item"):
 *   - the opener must be followed by a non-space character;
 *   - the closer must be preceded by a non-space character;
 *   - a single `*` closer is never half of a `**` pair.
 * Returns -1 when there is no valid closer, in which case the opener is literal.
 */
const findClosingMarker = (text: string, from: number, marker: '*' | '**'): number => {
  if (from >= text.length || isWhitespace(text[from])) return -1;
  let j = text.indexOf(marker, from);
  while (j !== -1) {
    const precededBySpace = isWhitespace(text[j - 1]);
    const partOfDoubleStar =
      marker === '*' && (text[j + 1] === '*' || (j > 0 && text[j - 1] === '*'));
    if (!precededBySpace && !partOfDoubleStar) return j;
    j = text.indexOf(marker, j + 1);
  }
  return -1;
};

/**
 * Return the address unchanged if it is safe to put in an href, otherwise null.
 * The scheme is checked twice on purpose: the prefix test rejects everything
 * that is not written as http:// or https://, and the URL parse rejects
 * addresses the browser itself would not accept. The upstream text is
 * untrusted, and an href is the one place in this file where a string from it
 * becomes behaviour instead of visible text.
 */
const safeHttpUrl = (raw: string): string | null => {
  if (!/^https?:\/\//i.test(raw)) return null;
  try {
    const { protocol } = new URL(raw);
    return protocol === 'http:' || protocol === 'https:' ? raw : null;
  } catch {
    return null;
  }
};

/**
 * Try to read a `[label](address)` link whose `[` is at `from`.
 * Deliberately narrow: the label has no `]` in it, `](` must be adjacent, and
 * the address runs to the first `)` and contains no whitespace. Anything that
 * does not fit, or whose address is not http(s), returns null and the caller
 * keeps the characters as literal text.
 */
const readLink = (
  text: string,
  from: number,
): { label: string; href: string; end: number } | null => {
  const labelEnd = text.indexOf(']', from + 1);
  if (labelEnd === -1 || text[labelEnd + 1] !== '(') return null;
  const urlEnd = text.indexOf(')', labelEnd + 2);
  if (urlEnd === -1) return null;
  const label = text.slice(from + 1, labelEnd);
  const address = text.slice(labelEnd + 2, urlEnd);
  if (!label.trim() || /\s/.test(address)) return null;
  const href = safeHttpUrl(address);
  return href ? { label, href, end: urlEnd + 1 } : null;
};

/** Tokenize one line into text / strong / em / link nodes. Exported for tests. */
export const parseInline = (text: string): InlineNode[] => {
  const nodes: InlineNode[] = [];
  let buffer = '';
  let i = 0;

  const flush = () => {
    if (buffer) {
      nodes.push({ type: 'text', value: buffer });
      buffer = '';
    }
  };

  while (i < text.length) {
    if (text[i] === '[') {
      const link = readLink(text, i);
      if (link) {
        flush();
        // The label goes back through the tokenizer so **bold** works inside a
        // link. The address never does: it is only ever used as the href.
        nodes.push({ type: 'link', href: link.href, children: parseInline(link.label) });
        i = link.end;
        continue;
      }
    }
    if (text[i] !== '*') {
      buffer += text[i];
      i += 1;
      continue;
    }
    const marker: '*' | '**' = text.startsWith('**', i) ? '**' : '*';
    const contentStart = i + marker.length;
    const close = findClosingMarker(text, contentStart, marker);
    if (close === -1) {
      // Unmatched marker: keep the stars as literal text.
      buffer += marker;
      i = contentStart;
      continue;
    }
    flush();
    nodes.push({
      type: marker === '**' ? 'strong' : 'em',
      children: parseInline(text.slice(contentStart, close)),
    });
    i = close + marker.length;
  }
  flush();
  return nodes;
};

/**
 * Split a content field into paragraphs of lines. Null, undefined, blank and the
 * upstream "na" placeholder all yield an empty list (render nothing - an empty
 * state is honest, CLAUDE.md §10). Exported for tests.
 */
export const parseRichText = (text: string | null | undefined): ParagraphLines[] => {
  if (typeof text !== 'string') return [];
  const normalised = text.replace(/\r\n?/g, '\n').replace(LEGACY_BR, '\n').trim();
  if (!normalised || normalised.toLowerCase() === 'na') return [];
  return normalised
    .split(PARAGRAPH_BREAK)
    .map((paragraph) =>
      paragraph
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map(parseInline),
    )
    .filter((lines) => lines.length > 0);
};

// Paragraph spacing lives here so every wrapper (Impact intro, Background intro,
// accordion body) gets the same rhythm. Font and colour come from the existing
// styled wrapper around <RichText>, so nothing visual changes except the stars
// disappearing. `font-family: inherit` is required: the global stylesheet gives
// every <p> the Oswald heading font, and without this the accordion body (whose
// Roboto is set on the MUI Typography <div> above us) would switch fonts.
const Paragraph = styled.p`
  margin: 0 0 1em;
  font-family: inherit;

  &:last-child {
    margin-bottom: 0;
  }
`;

// Same pink, underline and hover as the site's other inline text link
// (LivefeedLink in FarmerModal.styles.ts). Weight is inherited so a link inside
// **bold** is bold and a link in normal text is not; the global stylesheet would
// otherwise give every <a> weight 500 and no underline.
const Link = styled.a`
  color: #e5007d;
  font-weight: inherit;
  text-decoration: underline;
  cursor: pointer;
  overflow-wrap: anywhere;

  &:hover {
    color: #b3005f;
  }
`;

const renderInline = (nodes: InlineNode[], keyPrefix: string): React.ReactNode[] =>
  nodes.map((node, index) => {
    const key = `${keyPrefix}-${index}`;
    if (node.type === 'text') {
      return <React.Fragment key={key}>{node.value}</React.Fragment>;
    }
    if (node.type === 'strong') {
      return <strong key={key}>{renderInline(node.children, key)}</strong>;
    }
    if (node.type === 'link') {
      return (
        <Link key={key} href={node.href} target="_blank" rel="noopener noreferrer">
          {renderInline(node.children, key)}
        </Link>
      );
    }
    return <em key={key}>{renderInline(node.children, key)}</em>;
  });

interface RichTextProps {
  text: string | null | undefined;
}

export const RichText = ({ text }: RichTextProps) => {
  const paragraphs = parseRichText(text);
  if (paragraphs.length === 0) return null;

  return (
    <>
      {paragraphs.map((lines, p) => (
        <Paragraph key={p}>
          {lines.map((line, l) => (
            <React.Fragment key={l}>
              {l > 0 && <br />}
              {renderInline(line, `${p}-${l}`)}
            </React.Fragment>
          ))}
        </Paragraph>
      ))}
    </>
  );
};
